import { Action, combineReducers, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { createReduxEnhancer as createSentryReduxEnhancer } from '@sentry/react';

import { appSlice, appNamespace } from './app/store/slice';
import { billsSlice, billsNamespace } from './modules/bill/store/slice';
import { carsSlice, carsNamespace } from './modules/car/store/slice';
import { carUploadSlice, carUploadNamespace } from './modules/car/store/upload-slice';
import { commonSlice, commonNamespace } from './modules/common/store/slice';
import { driversSlice, driverNamespace } from './modules/driver/store/slice';
import {
  driverUploadSlice,
  driverUploadNamespace,
  myDriverUploadSlice,
  myDriverUploadNamespace,
} from './modules/driver/store/upload-slice';
import { earningsSlice, earningsNamespace } from './modules/earning/store/slice';
import { journeysSlice, journeyNamespace } from './modules/journey/store/slice';
import { fsmSlice, fsmNamespace } from './modules/onboarding/store/fsm-slice';
import { onboardingSlice, onboardingNamespace } from './modules/partner/store/slice';
import { partnerUploadSlice, partnerUploadNamespace } from './modules/partner/store/uploadSlice';
import { userNamespace, userSlice } from './modules/user/store/slice';
import { reset } from './rootActions';

const combinedReducer = combineReducers({
  [carUploadNamespace]: carUploadSlice.reducer,
  [driverUploadNamespace]: driverUploadSlice.reducer,
  [myDriverUploadNamespace]: myDriverUploadSlice.reducer,
  [carsNamespace]: carsSlice.reducer,
  [driverNamespace]: driversSlice.reducer,
  [billsNamespace]: billsSlice.reducer,
  [onboardingNamespace]: onboardingSlice.reducer,
  [appNamespace]: appSlice.reducer,
  [fsmNamespace]: fsmSlice.reducer,
  [commonNamespace]: commonSlice.reducer,
  [partnerUploadNamespace]: partnerUploadSlice.reducer,
  [journeyNamespace]: journeysSlice.reducer,
  [earningsNamespace]: earningsSlice.reducer,
  [userNamespace]: userSlice.reducer,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === reset.type) {
    return combinedReducer(undefined, action);
  }

  return combinedReducer(state, action);
};

type RootState = ReturnType<typeof combinedReducer>;

const createRootStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }),
    enhancers: [
      createSentryReduxEnhancer({
        attachReduxState: false,
        stateTransformer: () => null,
        actionTransformer: action => ({ type: action.type }),
      }),
    ],
    preloadedState: preloadedState as RootState | undefined,
  });

const rootStore = createRootStore();

type AppDispatch = ThunkDispatch<RootState, void, Action>;

export type { RootState, AppDispatch };
export { rootStore, createRootStore };
